import React, { useMemo, useRef, useState } from "react";

import DefaultProfile from "../../assets/images/default_profile_new.png";
import ProfileImg from "../profile-img";
import { cn } from "../ui/utils";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "./ui/conversation";
import { Message, MessageContent } from "./ui/message";
import { Response } from "./ui/response";
import RealtimeConversationBar from "./realtime-conversation-bar";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

type RealtimeFallbackAgentChatProps = {
  allocatedUserName?: string;
  avatarUrl: string;
  className?: string;
  debug?: boolean;
  agentId?: string;
  dynamicVariables?: Record<string, any>;
  mode?: "both" | "text" | "voice";
  onExit?: () => void;
};

function safeError(err: unknown) {
  if (err instanceof Error) {
    return {
      name: err.name,
      message: err.message,
      stack: err.stack,
    };
  }
  try {
    return { message: JSON.stringify(err) };
  } catch {
    return { message: String(err) };
  }
}

export default function RealtimeFallbackAgentChat({
  allocatedUserName,
  avatarUrl,
  className = "",
  debug = false,
  agentId: agentIdProp,
  dynamicVariables: dynamicVariablesProp,
  mode = "text",
  onExit,
}: RealtimeFallbackAgentChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastSentText, setLastSentText] = useState<string | null>(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  const lastSentTextRef = useRef<string | null>(null);
  const agentId = agentIdProp;

  const debugLog = useMemo(() => {
    return (...args: any[]) => {
      if (!debug) return;
      const ts = new Date().toISOString();
      // eslint-disable-next-line no-console
      console.log(`[E11][${ts}]`, ...args);
    };
  }, [debug]);

  const isTextMode = mode === "text";

  if (mode === "voice") {
    return (
      <div className={cn("mx-auto h-full w-full", className)}>
        <div className="absolute inset-0 flex items-center justify-center">
          <RealtimeConversationBar
            avatar={avatarUrl}
            allocatedUserName={allocatedUserName}
            className="w-full max-w-md h-full"
            agentId={agentId}
            dynamicVariables={{ ...(dynamicVariablesProp || {}) }}
            mode="voice"
            keyboardOpen={false}
            setKeyboardOpen={() => {}}
            debug={debug}
            autoStart
            onConnect={() => {
              debugLog("onConnect()");
              setMessages([]);
              setError(null);
              setLastSentText(null);
              lastSentTextRef.current = null;
            }}
            onDisconnect={() => {
              debugLog("onDisconnect()");
              setMessages([]);
              setLastSentText(null);
              lastSentTextRef.current = null;
              // TODO: Re-enable exit-on-disconnect once session lifecycle is stable.
              onExit?.();
            }}
            onSendMessage={(text) => {
              debugLog("onSendMessage()", { text });
              setLastSentText(text);
              lastSentTextRef.current = text;
              setMessages((prev) => [...prev, { role: "user", content: text }]);
            }}
            onMessage={(msg: any) => {
              debugLog("onMessage()", msg);
              const source = msg?.source;
              const text = msg?.message;

              if (!source || typeof text !== "string") {
                debugLog("onMessage(): ignored (unexpected shape)", {
                  source,
                  textType: typeof text,
                  raw: msg,
                });
                return;
              }

              if (source === "user" && text === lastSentTextRef.current) {
                debugLog("onMessage(): deduped typed user message");
                setLastSentText(null);
                lastSentTextRef.current = null;
                return;
              }

              setMessages((prev) => [
                ...prev,
                {
                  role: source === "user" ? "user" : "assistant",
                  content: text,
                },
              ]);
            }}
            onError={(err) => {
              const e = safeError(err);
              debugLog("onError()", e);
              setError(e.message || "Something went wrong while connecting.");
            }}
          />
        </div>
      </div>
    );
  }

  const bottomPadClass =
    mode === "text" ? "pb-24" : keyboardOpen ? "pb-40" : "pb-24";

  return (
    <div className={cn("mx-auto h-full w-full", className)}>
      <div className="absolute top-10 left-0 flex items-center justify-start gap-3 px-2 pb-2.5 w-full">
        <div>
          <ProfileImg avatar={DefaultProfile} small />
        </div>
        <div className="text-sm md:text-base leading-5 font-semibold text-primary-dark">
          <span>
            {allocatedUserName
              ? `Hi this is ${allocatedUserName}. How can I help you today?`
              : "Hi this is Anna. How can I help you today?"}
          </span>
        </div>
      </div>
      <Conversation className={cn("absolute inset-0 mt-24 px-2", bottomPadClass)}>
        <ConversationContent
          className={cn(
            "flex min-w-0 flex-col pb-6 p-0 mr-2",
            isTextMode ? "gap-1.5" : "gap-2",
          )}
        >
          {error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          ) : messages.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center">
              Start a conversation
            </div>
          ) : (
            messages.map((message, index) => {
              const isUser = message.role === "user";
              const prevRole = messages[index - 1]?.role;

              const showSpeakerLabel =
                mode === "text" && (index === 0 || prevRole !== message.role);

              if (mode === "text") {
                return (
                  <div
                    key={index}
                    className={cn(
                      "flex w-full flex-col",
                      isUser ? "items-end" : "items-start",
                    )}
                  >
                    {showSpeakerLabel && (
                      <div className="mb-1 text-xs text-muted-foreground flex items-center">
                        {!isUser && (
                          <img
                            // src={avatarUrl || DefaultProfile}
                            src={DefaultProfile}
                            alt="agent"
                            className="h-5 w-5 rounded-full mr-1"
                          />
                        )}
                        <span>{isUser ? "You" : (allocatedUserName || "Anna")}</span>
                      </div>
                    )}

                    <div
                      className={cn(
                        "max-w-[80%] whitespace-pre-wrap break-words rounded-xl px-4 py-2 text-sm",
                        isUser
                          ? "bg-primary text-white"
                          : "bg-[#F4F4F5] text-black",
                      )}
                    >
                      {message.content}
                    </div>
                  </div>
                );
              }

              return (
                <div key={index} className="flex w-full flex-col gap-1">
                  <Message from={message.role}>
                    <MessageContent className="min-w-0 max-w-full">
                      <Response className="w-auto whitespace-pre-wrap wrap-anywhere rounded-xl bg-[#E2E8F0]">
                        {message.content}
                      </Response>
                    </MessageContent>
                  </Message>
                </div>
              );
            })
          )}
        </ConversationContent>

        <ConversationScrollButton className="bottom-[100px]" />
      </Conversation>

      <div className="absolute bottom-0 left-0 right-0 flex justify-center">
        <RealtimeConversationBar
          avatar={avatarUrl}
          allocatedUserName={allocatedUserName}
          className="w-full max-w-2xl"
          agentId={agentId}
          dynamicVariables={{ ...(dynamicVariablesProp || {}) }}
          mode={mode === "text" ? "text" : "both"}
          keyboardOpen={keyboardOpen}
          setKeyboardOpen={setKeyboardOpen}
          debug={debug}
          autoStart
          onConnect={() => {
            debugLog("onConnect()");
            setMessages([]);
            setError(null);
            setLastSentText(null);
            lastSentTextRef.current = null;
          }}
          onDisconnect={() => {
            debugLog("onDisconnect()");
            setMessages([]);
            setLastSentText(null);
            lastSentTextRef.current = null;
            // TODO: Re-enable exit-on-disconnect once session lifecycle is stable.
            onExit?.();
          }}
          onSendMessage={(text) => {
            debugLog("onSendMessage()", { text });
            setLastSentText(text);
            lastSentTextRef.current = text;
            setMessages((prev) => [...prev, { role: "user", content: text }]);
          }}
          onMessage={(msg: any) => {
            debugLog("onMessage()", msg);

            const source = msg?.source;
            const text = msg?.message;

            if (!source || typeof text !== "string") {
              debugLog("onMessage(): ignored (unexpected shape)", {
                source,
                textType: typeof text,
                raw: msg,
              });
              return;
            }

            if (source === "user" && text === lastSentTextRef.current) {
              debugLog("onMessage(): deduped typed user message");
              setLastSentText(null);
              lastSentTextRef.current = null;
              return;
            }

            setMessages((prev) => [
              ...prev,
              { role: source === "user" ? "user" : "assistant", content: text },
            ]);
          }}
          onError={(err) => {
            const e = safeError(err);
            debugLog("onError()", e);
            setError(e.message || "Something went wrong while connecting.");
          }}
        />
      </div>
    </div>
  );
}
